import type { Violation } from '@project/contracts'
import { Money, Panel } from '../design/index.js'
import type { Advisory, DocumentDetail } from '../app/api.js'
import { formatDate, formatDateTime } from './format.js'
import type { InsightKind } from '@project/contracts'
import { NoticeBlock } from './feedback.js'
import { FigurePanels } from './ProjectFigures.js'

export interface SummaryTabProps {
  readonly doc: DocumentDetail
  /** `preview` is set for what submitting a draft would raise (BILL-051). */
  readonly onOpenExceptions: (violations: readonly Violation[], preview?: boolean) => void
  /** Jumps to one grid on the Form tab — the Details line counts (BILL-018). */
  readonly onOpenGrid?: (section: string) => void
  /** Opens one of the three order modals over this document (docs/06 §4). */
  readonly onOpenInsight?: (kind: InsightKind) => void
}

const ApprovalStrip = ({ doc }: { doc: DocumentDetail }) => (
  <div className="approvals">
    {doc.approvals.map((step) => (
      <div
        key={step.name}
        className={
          step.state === 'COMPLETED'
            ? 'approval approval--done'
            : step.state === 'CURRENT'
              ? 'approval approval--current'
              : 'approval'
        }
      >
        <div className="approval__name">{step.name}</div>
        <div className="approval__who">{step.user ?? (step.state === 'PENDING' ? 'Not yet with anyone' : '')}</div>
        <div className="approval__when">
          {step.at ? formatDateTime(step.at) : step.state === 'CURRENT' ? 'Waiting' : ''}
        </div>
      </div>
    ))}
  </div>
)

const AdvisoryRow = ({
  advisory,
  onOpenExceptions,
  onOpenInsight,
}: {
  advisory: Advisory
  onOpenExceptions: SummaryTabProps['onOpenExceptions']
  onOpenInsight: SummaryTabProps['onOpenInsight']
}) => (
  <NoticeBlock tone={advisory.tone}>
    <span>{advisory.text}</span>{' '}
    {advisory.violations && advisory.violations.length > 0 ? (
      <button type="button" className="link-button" onClick={() => onOpenExceptions(advisory.violations ?? [], advisory.preview === true)}>
        {advisory.actionLabel ?? 'Check exception details'}
      </button>
    ) : advisory.insight && onOpenInsight ? (
      <button type="button" className="link-button" onClick={() => onOpenInsight(advisory.insight!)}>
        {advisory.actionLabel ?? 'Open'}
      </button>
    ) : advisory.href ? (
      <a href={advisory.href}>{advisory.actionLabel ?? 'Open'}</a>
    ) : null}
  </NoticeBlock>
)

export const SummaryTab = ({ doc, onOpenExceptions, onOpenInsight, onOpenGrid }: SummaryTabProps) => (
  <>
    <Panel title="Approval progress">
      {doc.approvals.length === 0 ? (
        <p className="helper">This document has not been submitted for approval yet.</p>
      ) : (
        <ApprovalStrip doc={doc} />
      )}
    </Panel>

    {/*
      A type that words its own card (STK-009) has its title and one line —
      "PRO/256 - MMG - CUTTER PUMP", "Warehouse to Site • 21-Aug-2026 • MMG
      ASSETS LLP • Integration Pending" — and an Amount, as the reference.
    */}
    <Panel title={doc.card?.title || (doc.party?.label ?? 'This document')} aside={doc.card ? null : doc.headline}>
      <div className="row row--wrap" style={{ alignItems: 'flex-end', gap: 32 }}>
        {doc.card ? (
          <div>
            <div className="helper" data-testid="summary-card-line">{doc.card.line}</div>
          </div>
        ) : (
          <div>
            <div className="label">{doc.party ? 'Party' : 'Reference'}</div>
            <div className="figure figure--small">{doc.party?.label ?? doc.documentNo ?? 'Not numbered yet'}</div>
            {doc.party?.sublabel ? <div className="helper">{doc.party.sublabel}</div> : null}
            {/* "23-Jun-2026 • Integration Stopped • WO/CHK979" (BILL-017). */}
            {doc.integration || doc.references?.length ? (
              <div className="helper" aria-label="Status line">
                {[formatDate(doc.documentDate), doc.integration?.label, ...(doc.references ?? [])].filter(Boolean).join(' • ')}
              </div>
            ) : null}
            {/* A project: "Ongoing • 21-Aug-2026 • Fire Protection System Works". */}
            {doc.subline ? <div className="helper" data-testid="summary-subline">{doc.subline}</div> : null}
          </div>
        )}
        <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
          <div className="label">{doc.project || doc.card ? 'Amount' : 'Total'}</div>
          <div className="figure"><Money value={doc.total} size={28} /></div>
        </div>
      </div>
    </Panel>

    {/*
      docs/02 §4a separates the two: anything wrong with the document sits under
      a red "Needs Attention", and the analytical links sit under "Information".
      One list of eight notices with the important one third is a list nobody
      reads.
    */}
    {doc.advisories.some((a) => a.tone !== 'info') ? (
      <Panel title="Needs attention">
        <div className="stack">
          {doc.advisories.filter((a) => a.tone !== 'info').map((advisory) => (
            <AdvisoryRow
              key={advisory.key}
              advisory={advisory}
              onOpenExceptions={onOpenExceptions}
              onOpenInsight={onOpenInsight}
            />
          ))}
        </div>
      </Panel>
    ) : null}

    {doc.advisories.some((a) => a.tone === 'info') ? (
      <Panel title="Information">
        <div className="stack">
          {doc.advisories.filter((a) => a.tone === 'info').map((advisory) => (
            <AdvisoryRow
              key={advisory.key}
              advisory={advisory}
              onOpenExceptions={onOpenExceptions}
              onOpenInsight={onOpenInsight}
            />
          ))}
        </div>
      </Panel>
    ) : null}

    <Panel title="Details">
      {doc.details.length === 0 ? (
        <p className="helper">Nothing recorded yet.</p>
      ) : (
        <dl className="detail-list">
          {doc.details.map((entry) => (
            <div key={entry.label} style={{ display: 'contents' }}>
              <dt>{entry.label}</dt>
              <dd>
                {entry.section && onOpenGrid ? (
                  <button type="button" className="link-button" onClick={() => onOpenGrid(entry.section!)}>
                    {entry.value}
                  </button>
                ) : (entry.value || 'Not set')}
              </dd>
            </div>
          ))}
        </dl>
      )}
      {/*
        A project's Project Value Summary and Estimated Cost Summary sit inside
        Details, below a rule, side by side (PROJ1-014). They already carry
        its amount, so the generic Amount summary is not drawn twice.
      */}
      {doc.panels && doc.panels.length > 0 ? (
        <div style={{ borderTop: '1px solid var(--line)', marginTop: 20, paddingTop: 20 }}>
          <FigurePanels panels={doc.panels} />
        </div>
      ) : null}
    </Panel>

    {doc.panels && doc.panels.length > 0 ? null : (
      <Panel title="Amount summary">
        <table className="totals">
          <tbody>
            {doc.amounts.map((line) => (
              <tr key={line.label} className={line.emphasis ? 'is-total' : undefined}>
                <td>{line.label}</td>
                <td><Money value={line.amount} /></td>
              </tr>
            ))}
            <tr className="is-total">
              {/* After deductions it is what is payable (BILL-017). */}
              <td>{doc.totalValue && doc.totalValue.adjustments.length > 0 ? 'Net total' : 'Total'}</td>
              <td><Money value={doc.total} size={17} /></td>
            </tr>
          </tbody>
        </table>
      </Panel>
    )}
  </>
)
